<template>
  <div class="dynamic-news">
    <div class="section-header">
      <div class="section-icon">📰</div>
      <h2 class="section-title">动态新闻</h2>
      <div class="refresh-btn" @click="refreshNews" :class="{ 'loading': isLoading }">
        <i class="el-icon-refresh"></i>
      </div>
    </div>
    
    <div class="news-list" v-if="newsList.length > 0" ref="newsList">
      <div 
        v-for="news in newsList" 
        :key="news.alpha_id" 
        class="news-item"
        @click="openNewsDetail(news)"
      >
        <div class="news-header">
          <div class="news-title" :title="news.title">{{ news.title }}</div>
          <div class="news-meta">
            <span class="news-time">{{ formatTime(news.time_published) }}</span>
            <span class="news-source">{{ news.source }}</span>
          </div>
        </div>
        
        <div class="news-content">
          <div class="news-summary">{{ news.summary }}</div>
          <div class="news-image" v-if="news.banner_image">
            <img :src="news.banner_image" :alt="news.title" @error="handleImageError">
          </div>
        </div>
        
        <div class="news-footer">
          <div class="sentiment-info">
            <span 
              class="sentiment-badge" 
              :class="getSentimentClass(news.overall_sentiment_label)"
            >
              {{ getSentimentText(news.overall_sentiment_label) }}
            </span>
            <span class="sentiment-score">{{ Number(news.overall_sentiment_score).toFixed(3) }}</span>
          </div>
          
          <div class="tickers" v-if="news.ticker && news.ticker.length > 0">
            <span 
              v-for="ticker in news.ticker.slice(0, 3)" 
              :key="ticker.alpha_ticker_id"
              class="ticker-tag"
              :title="`相关性: ${Number(ticker.relevance_score).toFixed(3)}`"
            >
              {{ ticker.ticker }}
            </span>
          </div>
        </div>
      </div>
      
      <!-- 加载更多提示 -->
      <div v-if="isLoadingMore" class="loading-more">
        <i class="el-icon-loading"></i>
        <span>加载更多新闻...</span>
      </div>
      
      <!-- 没有更多数据提示 -->
      <div v-if="!hasMore && newsList.length > 0" class="no-more">
        <span>已加载全部新闻</span>
      </div>
    </div>
    
    <div class="loading-state" v-else-if="isLoading && currentPage === 1">
      <div class="loading-spinner">
        <i class="el-icon-loading"></i>
      </div>
      <p>正在加载新闻...</p>
    </div>
    
    <div class="empty-state" v-else-if="!isLoading && newsList.length === 0">
      <div class="empty-icon">📭</div>
      <p>暂无新闻数据</p>
      <button class="retry-btn" @click="loadNews">重试</button>
    </div>
    
  </div>
</template>

<script>
import { getAlphaNewsList } from '@/http/api/news'
import { Message } from 'element-ui'

export default {
  name: 'DynamicNews',
  data() {
    return {
      newsList: [],
      isLoading: false,
      isLoadingMore: false,
      currentPage: 1,
      pageSize: 10,
      hasMore: true,
      error: null,
      scrollHandler: null,
      scrollTimer: null
    }
  },
  
  mounted() {
    this.loadNews()
    // 延迟初始化滚动监听器，确保 DOM 完全渲染
    setTimeout(() => {
      this.initScrollListener()
    }, 500)
  },
  
  beforeDestroy() {
    this.removeScrollListener()
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer)
    }
  },
  
  methods: {
    async loadNews(isLoadMore = false) {
      if (isLoadMore) {
        if (this.isLoadingMore || !this.hasMore) return
        this.isLoadingMore = true
      } else {
        this.isLoading = true
        this.currentPage = 1
        this.hasMore = true
        this.newsList = []
      }
      
      this.error = null
      
      try {
        const params = {
          page: this.currentPage,
          limit: this.pageSize
        }
        
        const response = await getAlphaNewsList(params)
        
        let newData = []
        // 处理API响应
        if (Array.isArray(response)) {
          newData = response
        } else if (response && Array.isArray(response.result)) {
          newData = response.result
        } else {
          newData = this.getMockData()
        }
        
        
        if (isLoadMore) {
          // 追加数据
          this.newsList = [...this.newsList, ...newData]
        } else {
          // 替换数据
          this.newsList = newData
        }
        
        // 检查是否还有更多数据
        if (newData.length < this.pageSize) {
          this.hasMore = false
        } else {
          this.currentPage++
        }
        
        // 加载完成后再次检查是否需要继续加载
        if (isLoadMore) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.checkScrollPosition()
            }, 100)
          })
        }
        
      } catch (error) {
        this.error = error
        
        // 如果API调用失败，使用Mock数据作为备选
        if (!isLoadMore) {
          this.newsList = this.getMockData()
          
          // 只在Mock数据也为空时显示错误
          if (this.newsList.length === 0) {
            Message.error('加载新闻失败，请稍后重试')
          }
        }
      } finally {
        if (isLoadMore) {
          this.isLoadingMore = false
        } else {
          this.isLoading = false
        }
      }
    },
    
    async refreshNews() {
      if (this.isLoading || this.isLoadingMore) return
      await this.loadNews(false)
      Message.success('新闻已刷新')
    },
    
    initScrollListener() {
      this.$nextTick(() => {
        const newsList = this.$refs.newsList
        
        if (newsList) {
          // 创建绑定了正确this上下文的处理函数
          this.scrollHandler = this.handleScroll.bind(this)
          newsList.addEventListener('scroll', this.scrollHandler, { passive: true })
          
          // 立即检查一次是否需要加载更多
          this.checkScrollPosition()
        } else {
        }
      })
    },
    
    removeScrollListener() {
      const newsList = this.$refs.newsList
      if (newsList && this.scrollHandler) {
        newsList.removeEventListener('scroll', this.scrollHandler)
      }
    },
    
    handleScroll() {
      // 防抖处理，避免频繁触发
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer)
      }
      
      this.scrollTimer = setTimeout(() => {
        this.checkScrollPosition()
      }, 50) // 减少防抖时间提高响应速度
    },
    
    checkScrollPosition() {
      const newsList = this.$refs.newsList
      
      if (!newsList) {
        return
      }
      
      if (this.isLoadingMore) {
        return
      }
      
      if (!this.hasMore) {
        return
      }
      
      const { scrollTop, scrollHeight, clientHeight } = newsList
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
      
      
      // 当滚动到距离底部200px时加载更多
      if (distanceFromBottom <= 200) {
        this.loadNews(true)
      }
    },
    
    
    formatTime(timeStr) {
      if (!timeStr) return ''
      
      try {
        const date = new Date(timeStr)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
        const diffDays = Math.floor(diffHours / 24)
        
        if (diffDays > 0) {
          return `${diffDays}天前`
        } else if (diffHours > 0) {
          return `${diffHours}小时前`
        } else {
          const diffMinutes = Math.floor(diffMs / (1000 * 60))
          return diffMinutes > 0 ? `${diffMinutes}分钟前` : '刚刚'
        }
      } catch (error) {
        return timeStr
      }
    },
    
    getSentimentClass(label) {
      const classMap = {
        'Bullish': 'bullish',
        'Somewhat-Bullish': 'somewhat-bullish',
        'Neutral': 'neutral',
        'Somewhat-Bearish': 'somewhat-bearish',
        'Bearish': 'bearish'
      }
      return classMap[label] || 'neutral'
    },
    
    getSentimentText(label) {
      const textMap = {
        'Bullish': '看涨',
        'Somewhat-Bullish': '偏涨',
        'Neutral': '中性',
        'Somewhat-Bearish': '偏跌',
        'Bearish': '看跌'
      }
      return textMap[label] || '中性'
    },
    
    openNewsDetail(news) {
      if (news.url) {
        window.open(news.url, '_blank')
      }
    },
    
    handleImageError(event) {
      event.target.style.display = 'none'
    },
    
    getMockData() {
      // Mock数据，模拟真实接口返回的数据结构
      // 为了测试懒加载，增加更多数据
      const baseMockNews = [
        {
          alpha_id: 1,
          title: "ROSEN, A TOP-RANKED LAW FIRM, Encourages Savara Inc. Investors to Secure Counsel Before Important Deadline in Securities Class Action- SVRA - Savara  ( NASDAQ:SVRA )",
          url: "https://www.benzinga.com/pressreleases/25/09/g47833741/rosen-a-top-ranked-law-firm-encourages-savara-inc-investors-to-secure-counsel-before-important-dea",
          time_published: "2025-09-24 10:54:00",
          summary: "NEW YORK, Sept. 23, 2025 ( GLOBE NEWSWIRE ) -- WHY: Rosen Law Firm, a global investor rights law firm, reminds purchasers of securities of Savara Inc. SVRA between March 7, 2024 and May 23, 2025, both dates inclusive ( the \"Class Period\" ) , of the important November 7, 2025 lead plaintiff ...",
          banner_image: "https://www.benzinga.com/next-assets/images/schema-image-default.png",
          source: "Benzinga",
          source_domain: "www.benzinga.com",
          overall_sentiment_score: "0.112473",
          overall_sentiment_label: "Neutral",
          author: "Globe Newswire",
          ticker: [
            {
              alpha_ticker_id: 1,
              ticker: "META",
              relevance_score: "0.062926",
              ticker_sentiment_score: "0.000000",
              ticker_sentiment_label: "Neutral",
              alpha_id: 1
            },
            {
              alpha_ticker_id: 2,
              ticker: "SVRA",
              relevance_score: "0.419484",
              ticker_sentiment_score: "-0.008740",
              ticker_sentiment_label: "Neutral",
              alpha_id: 1
            }
          ],
          topic: [
            {
              alpha_topic_id: 1,
              topic_name: "Life Sciences",
              relevance_score: "0.500000",
              alpha_id: 1
            },
            {
              alpha_topic_id: 2,
              topic_name: "Technology",
              relevance_score: "0.500000",
              alpha_id: 1
            }
          ]
        },
        {
          alpha_id: 2,
          title: "Tim Cook Revives Apple's First International Store In Exclusive Luxury Location That Embodies Bernard Arnault's Advice To Steve Jobs - Apple  ( NASDAQ:AAPL )",
          url: "https://www.benzinga.com/markets/tech/25/09/47833732/tim-cook-revives-apples-first-international-store-in-exclusive-luxury-location-that-embodies-bernard",
          time_published: "2025-09-24 10:46:29",
          summary: "Apple Inc. AAPL will reopen its Ginza store in Tokyo on Friday, marking a homecoming to the brand's original international retail location across from Louis Vuitton in the city's luxury shopping district. Apple shares slipped 0.64% on Tuesday and edged lower in after-hours trading, according to ...",
          banner_image: "https://cdn.benzinga.com/files/images/story/2025/09/23/Milan--Italy-january-10--2019-Apple-Stor.jpeg?width=1200&height=800&fit=crop",
          source: "Benzinga",
          source_domain: "www.benzinga.com",
          overall_sentiment_score: "0.277582",
          overall_sentiment_label: "Somewhat-Bullish",
          author: "Ananya Gairola",
          ticker: [
            {
              alpha_ticker_id: 3,
              ticker: "AAPL",
              relevance_score: "0.852890",
              ticker_sentiment_score: "0.476478",
              ticker_sentiment_label: "Bullish",
              alpha_id: 2
            }
          ],
          topic: [
            {
              alpha_topic_id: 4,
              topic_name: "Technology",
              relevance_score: "1.000000",
              alpha_id: 2
            }
          ]
        },
        {
          alpha_id: 3,
          title: "中国科技股迎来新机遇，人工智能领域投资热度持续升温",
          url: "#",
          time_published: "2025-09-26 08:30:00",
          summary: "随着人工智能技术的快速发展，中国科技股市场迎来新的投资机遇。多家知名投资机构表示，AI相关产业链将在未来几年内保持高速增长态势，相关公司估值有望进一步提升...",
          banner_image: "",
          source: "财经新闻",
          source_domain: "finance.example.com",
          overall_sentiment_score: "0.456789",
          overall_sentiment_label: "Bullish",
          author: "张三",
          ticker: [
            {
              alpha_ticker_id: 4,
              ticker: "BABA",
              relevance_score: "0.75",
              ticker_sentiment_score: "0.4",
              ticker_sentiment_label: "Bullish",
              alpha_id: 3
            }
          ],
          topic: [
            {
              alpha_topic_id: 5,
              topic_name: "Artificial Intelligence",
              relevance_score: "0.9",
              alpha_id: 3
            }
          ]
        },
        {
          alpha_id: 4,
          title: "全球能源转型加速，新能源汽车销量创历史新高",
          url: "#",
          time_published: "2025-09-26 06:15:00",
          summary: "据最新统计数据显示，今年第三季度全球新能源汽车销量同比增长35%，创下历史新高。业内专家认为，政策支持和技术进步是推动新能源汽车市场快速发展的主要因素...",
          banner_image: "",
          source: "汽车资讯",
          source_domain: "auto.example.com",
          overall_sentiment_score: "0.325",
          overall_sentiment_label: "Somewhat-Bullish",
          author: "李四",
          ticker: [
            {
              alpha_ticker_id: 5,
              ticker: "TSLA",
              relevance_score: "0.85",
              ticker_sentiment_score: "0.3",
              ticker_sentiment_label: "Bullish",
              alpha_id: 4
            }
          ],
          topic: [
            {
              alpha_topic_id: 6,
              topic_name: "Automotive",
              relevance_score: "0.95",
              alpha_id: 4
            }
          ]
        }
      ]
      
      // 生成更多数据用于测试懒加载
      const mockNews = []
      for (let i = 0; i < 50; i++) {
        baseMockNews.forEach((news, index) => {
          mockNews.push({
            ...news,
            alpha_id: i * baseMockNews.length + index + 1,
            title: `[第${i + 1}批] ${news.title}`,
            time_published: new Date(Date.now() - (i * baseMockNews.length + index) * 3600000).toISOString()
          })
        })
      }
      
      // 根据页数返回不同的数据
      const startIndex = (this.currentPage - 1) * this.pageSize
      const endIndex = startIndex + this.pageSize
      return mockNews.slice(startIndex, endIndex)
    }
  }
}
</script>

<style lang="scss" scoped>
.dynamic-news {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
  
  .section-icon {
    font-size: 18px;
    margin-right: 8px;
    color: #3498db;
  }
  
  .section-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    color: #333;
    flex: 1;
  }
  
  .refresh-btn {
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.3s ease;
    color: #666;
    
    &:hover {
      background: #e9ecef;
      color: #3498db;
    }
    
    &.loading {
      pointer-events: none;
      
      i {
        animation: spin 1s linear infinite;
      }
    }
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.news-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
  }
}

.news-item {
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #f0f0f0;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
    border-color: #3498db;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
}

.news-header {
  margin-bottom: 12px;
  
  .news-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    line-height: 1.4;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .news-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #999;
    
    .news-time {
      color: #666;
    }
    
    .news-source {
      background: #f8f9fa;
      padding: 2px 6px;
      border-radius: 4px;
      color: #666;
    }
  }
}

.news-content {
  margin-bottom: 12px;
  
  .news-summary {
    font-size: 13px;
    color: #666;
    line-height: 1.5;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .news-image {
    width: 100%;
    height: 80px;
    border-radius: 6px;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
    
    &:hover img {
      transform: scale(1.05);
    }
  }
}

.news-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  
  .sentiment-info {
    display: flex;
    align-items: center;
    gap: 6px;
    
    .sentiment-badge {
      padding: 2px 6px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 500;
      
      &.bullish {
        background: #d4edda;
        color: #155724;
      }
      
      &.somewhat-bullish {
        background: #d1ecf1;
        color: #0c5460;
      }
      
      &.neutral {
        background: #f8f9fa;
        color: #6c757d;
      }
      
      &.somewhat-bearish {
        background: #fdeaa7;
        color: #856404;
      }
      
      &.bearish {
        background: #f8d7da;
        color: #721c24;
      }
    }
    
    .sentiment-score {
      font-size: 11px;
      color: #999;
    }
  }
  
  .tickers {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    
    .ticker-tag {
      background: #e9ecef;
      color: #495057;
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 500;
      cursor: help;
    }
  }
}

.loading-state, .empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  padding: 40px 20px;
  
  .loading-spinner, .empty-icon {
    font-size: 32px;
    margin-bottom: 16px;
  }
  
  .loading-spinner i {
    animation: spin 1s linear infinite;
  }
  
  p {
    margin-bottom: 16px;
    font-size: 14px;
  }
  
  .retry-btn {
    background: #3498db;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    
    &:hover {
      background: #2980b9;
    }
  }
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: #666;
  font-size: 14px;
  
  i {
    animation: spin 1s linear infinite;
  }
}

.no-more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #999;
  font-size: 13px;
  border-top: 1px solid #f0f0f0;
  
  span {
    position: relative;
    padding: 0 20px;
    background: white;
    
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      width: 50px;
      height: 1px;
      background: #e0e0e0;
    }
    
    &::before {
      left: -70px;
    }
    
    &::after {
      right: -70px;
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .news-item {
    padding: 12px;
    margin-bottom: 8px;
  }
  
  .news-header .news-title {
    font-size: 13px;
  }
  
  .news-content .news-summary {
    font-size: 12px;
  }
  
  .news-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
}
</style>
